<script setup lang="ts">
import { computed } from "vue";
import type { SyncStatus } from "../../sync/types.js";

/**
 * Tells the user where their scores actually are.
 *
 * The requirement is that people are informed when scoring offline and when it
 * reconciles -- so the badge is never silent about unsent turns, and says
 * plainly when everything has landed. A rejected turn is the loud case: a score
 * is missing and no amount of waiting will fix it.
 */

const props = defineProps<{ status: SyncStatus }>();

type Tone = "ok" | "busy" | "wait" | "bad";

const view = computed<{ tone: Tone; text: string }>(() => {
  const s = props.status;
  if (s.rejected > 0) {
    return { tone: "bad", text: `${s.rejected} Eintrag abgelehnt` };
  }
  if (s.syncing) return { tone: "busy", text: "Wird übertragen …" };
  if (!s.online) {
    return s.pending > 0
      ? { tone: "wait", text: `Offline · ${s.pending} gespeichert` }
      : { tone: "wait", text: "Offline" };
  }
  if (s.pending > 0) return { tone: "wait", text: `${s.pending} ausstehend` };
  return { tone: "ok", text: "Gespeichert" };
});
</script>

<template>
  <!-- polite: a score entry must never be interrupted by this announcement. -->
  <div
    class="badge"
    :class="`badge--${view.tone}`"
    role="status"
    aria-live="polite"
  >
    <span
      class="pip"
      aria-hidden="true"
    />
    <span class="text">{{ view.text }}</span>
  </div>
</template>

<style scoped>
.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  background: var(--card);
  color: var(--text-2);
}

.pip {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  flex: none;
}

.badge--ok {
  color: var(--text-3);
}

.badge--busy,
.badge--wait {
  color: var(--accent);
  background: var(--accent-tint);
}

.badge--busy .pip {
  animation: pulse 1.1s ease-in-out infinite;
}

.badge--bad {
  color: #fff;
  background: var(--danger);
}

@keyframes pulse {
  50% {
    opacity: 0.25;
  }
}
</style>
